import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Preferences } from '@capacitor/preferences';

import { useAuthContext } from '@/domains/auth/authContext';
import {
  isBiometricAvailable,
  isBiometricEnabled,
} from '@/domains/shared/components/biometrics/Biometrics';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';
import { isNative } from '@/domains/shared/utils/native';

/**
 * IdentityBootContext owns the one-time orchestration that has to happen
 * before the PIN unlock and biometric prompt code can run safely:
 *
 *   1. Firebase auth has restored its session (gated by `authReady` upstream).
 *   2. Identity prefs (`PIN_KEY`, `BIOMETRIC_ENABLED`, and the OS biometric
 *      availability) are read once and cached. PinManager and
 *      BiometricsProvider consume these values instead of doing their own
 *      reads, so they share a consistent snapshot.
 *
 * Consumers gate on `phase === 'ready'` (or the `ready` boolean). Until
 * then the values returned for `pinSet` / `biometricAvailable` /
 * `biometricEnabled` are meaningless (default `false`).
 */

const PIN_KEY = 'PIN_KEY';

const LOG_PREFIX = '[IDENTITY_BOOT]';

type Phase = 'pending' | 'loading-identity' | 'ready';

interface IdentityBootValue {
  phase: Phase;
  ready: boolean;
  pinSet: boolean;
  biometricAvailable: boolean;
  biometricEnabled: boolean;
  /** Notify the boot context that the cached `pinSet` value has changed
   *  (e.g. after `PinSetup` writes a new PIN, or after logout clears it). */
  setPinSet: (value: boolean) => void;
  /** Notify the boot context that the cached `biometricEnabled` value has
   *  changed (e.g. user toggled biometrics in account settings). */
  setBiometricEnabled: (value: boolean) => void;
  /** Re-read identity prefs from storage after logout/login. */
  refresh: () => Promise<void>;
}

const IdentityBootContext = createContext<IdentityBootValue | undefined>(
  undefined,
);

const readIdentityState = async () => {
  // PIN and biometrics are native-only concerns. On web, Capacitor's
  // Preferences plugin is backed by `localStorage`, which means a value
  // written during a prior native simulator session — or any code that
  // ignored the native gate — would otherwise leak into the cached state
  // and show up in the unlock machine context. Returning all-false on web
  // keeps the contract tight: identity prefs are never read or written
  // outside native builds.
  if (!isNative()) {
    return {
      pinSet: false,
      biometricAvailable: false,
      biometricEnabled: false,
    };
  }

  try {
    const [pinResult, biometricEnabledResult, biometricAvailableResult] =
      await Promise.all([
        Preferences.get({ key: PIN_KEY }),
        isBiometricEnabled(),
        isBiometricAvailable(),
      ]);

    return {
      pinSet: !!pinResult.value,
      biometricAvailable: biometricAvailableResult,
      biometricEnabled: biometricEnabledResult,
    };
  } catch (error) {
    // A rejected read (Preferences.get, biometric checks) must never
    // surface as an unhandled rejection — `refresh()` is called
    // fire-and-forget from the currentUser-change effect, and the boot
    // sequence runs detached too. Fall back to safe defaults so the
    // unlock machine routes to a sane state (signed-out / PIN entry)
    // instead of IdentityBoot silently stalling or crashing.
    logToDatadog(
      'error',
      `${LOG_PREFIX} readIdentityState failed — falling back to safe defaults`,
      { error: error instanceof Error ? error.message : String(error) },
    );
    return {
      pinSet: false,
      biometricAvailable: false,
      biometricEnabled: false,
    };
  }
};

export const IdentityBootProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { auth, authReady, currentUser } = useAuthContext();

  const [phase, setPhase] = useState<Phase>('pending');
  const [pinSet, setPinSetState] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabledState] = useState(false);

  // One-shot guard. We want the initial reads to run exactly once per
  // provider lifetime. Subsequent identity
  // refreshes (e.g. after logout) go through the `refresh()` callback
  // explicitly — never through a re-run of this effect.
  const bootStartedRef = useRef(false);
  const bootDoneRef = useRef(false);

  const refresh = useCallback(async () => {
    const result = await readIdentityState();
    setPinSetState(result.pinSet);
    setBiometricAvailable(result.biometricAvailable);
    setBiometricEnabledState(result.biometricEnabled);
    logToDatadog('info', `${LOG_PREFIX} refresh complete`, result);
  }, []);

  useEffect(() => {
    if (!authReady || !auth) return;
    if (bootStartedRef.current) return;
    bootStartedRef.current = true;

    let cancelled = false;

    const boot = async () => {
      logToDatadog('info', `${LOG_PREFIX} boot starting`, {
        hasCurrentUser: !!currentUser,
        isNative: isNative(),
      });

      if (cancelled) return;

      setPhase('loading-identity');
      const result = await readIdentityState();
      if (cancelled) return;

      setPinSetState(result.pinSet);
      setBiometricAvailable(result.biometricAvailable);
      setBiometricEnabledState(result.biometricEnabled);
      setPhase('ready');
      bootDoneRef.current = true;

      logToDatadog('info', `${LOG_PREFIX} boot complete`, result);
    };

    boot();

    return () => {
      cancelled = true;
    };
    // The ref-guard keeps boot from restarting when auth state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, auth]);

  // Post-boot identity refresh: after logout (Preferences.clear) and a new
  // login, the cached values for `pinSet`/`biometricEnabled` go stale.
  // Re-read identity whenever the Firebase user changes after boot.
  //
  // The `bootDoneRef` gate is what makes this safe: while boot is still
  // running, mid-flight `currentUser` changes trip this effect but exit early without
  // refreshing. Once boot finishes, the next genuine user change refreshes.
  const uid = currentUser?.uid ?? null;
  useEffect(() => {
    if (!bootDoneRef.current) return;

    logToDatadog('info', `${LOG_PREFIX} user changed after boot, refreshing`, {
      newUid: uid,
    });
    refresh();
  }, [uid, refresh]);

  const value: IdentityBootValue = {
    phase,
    ready: phase === 'ready',
    pinSet,
    biometricAvailable,
    biometricEnabled,
    setPinSet: setPinSetState,
    setBiometricEnabled: setBiometricEnabledState,
    refresh,
  };

  return (
    <IdentityBootContext.Provider value={value}>
      {children}
    </IdentityBootContext.Provider>
  );
};

export const useIdentityBoot = () => {
  const ctx = useContext(IdentityBootContext);
  if (!ctx) {
    throw new Error(
      'useIdentityBoot must be used within an IdentityBootProvider',
    );
  }
  return ctx;
};
